import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Seo, { SITE_URL } from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { siteInfo } from "@/mocks/site";

const principles = [
  {
    icon: "ri-focus-3-line",
    title: "只做轻松向内容",
    text: "我们不做沉重议题，也不制造焦虑。瓜乐圈存在的意义，是让你在忙碌间隙里喘口气。",
  },
  {
    icon: "ri-shield-check-line",
    title: "先确认再整理",
    text: "每一条内容在整理之前都会先核对来源与时间线，不确定的部分宁可留白，也不随意下结论。",
  },
  {
    icon: "ri-user-heart-line",
    title: "尊重每一位当事人",
    text: "我们点评的是内容本身，不是人。涉及个人隐私与未公开信息的内容，一律不收录。",
  },
];

const values = [
  {
    icon: "ri-lightbulb-flash-line",
    title: "轻松优先",
    text: "打开就能看，看完就能关。不设连续追更的门槛，也不需要记住前面的剧情。",
  },
  {
    icon: "ri-layout-masonry-line",
    title: "整理胜过堆砌",
    text: "与其把内容一股脑塞给你，不如先分好类、排好序，让每一次浏览都有清晰的路径。",
  },
  {
    icon: "ri-heart-2-line",
    title: "快乐可复用",
    text: "好的乐子值得被反复回看。我们会把耐看的内容沉淀成合集，随时都能重新翻出来。",
  },
];

const voices = [
  {
    name: "晚睡的阿柚",
    role: "通勤读者",
    text: "每天地铁上刷两条，刚好到站，节奏卡得特别舒服。乐子合集是我周一的续命包。",
  },
  {
    name: "摸鱼观察员",
    role: "办公室读者",
    text: "要点速览设计得很贴心，先扫三句话，觉得有意思再往下看，完全不浪费时间。",
  },
  {
    name: "爱吃瓜的小满",
    role: "睡前读者",
    text: "小甜瓜栏目真的很容易上头，但不吵，看着看着就放松了，比刷短视频舒服。",
  },
];

export default function AboutPage() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: `关于${siteInfo.name}`,
      url: `${SITE_URL}/about`,
      inLanguage: "zh-CN",
      description: siteInfo.intro,
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: siteInfo.name,
      url: SITE_URL,
      email: siteInfo.email,
      description: siteInfo.slogan,
      foundingDate: String(siteInfo.startYear),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_URL}/about` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title="关于我们 — 瓜乐圈轻娱乐内容站的定位与内容原则"
        description="了解瓜乐圈：一个专注福利吃瓜的轻娱乐内容站，介绍品牌定位、发展理念、内容原则、读者口碑与联系方式。"
        keywords="关于瓜乐圈,福利吃瓜,内容原则,轻娱乐内容站"
        path="/about"
        jsonLd={jsonLd}
      />
      <Navbar />

      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-7">
                <p className="inline-flex w-fit items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                  <i className="ri-leaf-line"></i>
                  始于 {siteInfo.startYear}
                </p>
                <h1 className="mt-5 font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-5xl">
                  关于{siteInfo.name}
                </h1>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  {siteInfo.intro}
                  我们相信快乐不需要复杂的理由，也不该被冗长的内容填满。所以从第一天起，
                  {siteInfo.name} 就只做一件事：把值得一看的内容挑出来，整理成你随时能打开的样子。
                </p>
              </div>
              <div className="lg:col-span-5">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: "陪伴读者", value: siteInfo.readers },
                    { label: "内容栏目", value: "4 个" },
                    { label: "每日精选", value: `${siteInfo.dailyPicks} 条` },
                    { label: "更新频率", value: "每天" },
                  ].map((item) => (
                    <div key={item.label} className="rounded-xl2 border border-background-200 bg-background-50 p-5">
                      <p className="font-heading text-2xl font-bold text-foreground-950">{item.value}</p>
                      <p className="mt-1 text-xs text-foreground-500">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-background-200 bg-background-50">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
            <div className="max-w-2xl">
              <p className="mb-2 flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-primary-600">
                <span className="h-1 w-6 rounded-full bg-primary-500"></span>
                POSITIONING
              </p>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
                我们是一家什么样的内容站
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
                简单说，{siteInfo.name} 是一个把「福利吃瓜」做轻的站点。不追热点节奏，不制造对立，
                也不要求你连续追更。我们把名场面、爆笑高能、小甜瓜和乐子合集四个栏目各自做好，
                你按心情挑一个进去就行。
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {values.map((item) => (
                <div
                  key={item.title}
                  className="rounded-xl2 border border-background-200 bg-background-50 p-6 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="principle" className="scroll-mt-24 border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <p className="mb-2 flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-secondary-600">
                  <span className="h-1 w-6 rounded-full bg-secondary-500"></span>
                  PRINCIPLES
                </p>
                <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
                  内容原则
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  这三条是我们写每一篇内容之前都会过一遍的底线，也是判断一条内容该不该收录的标准。
                </p>
              </div>
              <div className="lg:col-span-8">
                <div className="space-y-4">
                  {principles.map((item, index) => (
                    <div
                      key={item.title}
                      className="flex gap-5 rounded-xl2 border border-background-200 bg-background-50 p-5 md:p-6"
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary-500 text-background-50">
                        <i className={`${item.icon} text-xl`}></i>
                      </span>
                      <div>
                        <p className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                          <span className="text-xs text-foreground-400">0{index + 1}</span>
                          {item.title}
                        </p>
                        <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="voice" className="scroll-mt-24 border-b border-background-200 bg-background-50">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
            <div className="mb-8 max-w-2xl">
              <p className="mb-2 flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-accent-700">
                <span className="h-1 w-6 rounded-full bg-accent-500"></span>
                READERS' VOICE
              </p>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
                读者怎么说
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                下面的留言来自订阅邮件和站内反馈，我们把它们原样保留下来，作为内容方向的一次参考。
              </p>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {voices.map((voice) => (
                <figure key={voice.name} className="flex flex-col rounded-xl2 border border-background-200 bg-background-50 p-6">
                  <i className="ri-double-quotes-l text-2xl text-accent-400"></i>
                  <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                    {voice.text}
                  </blockquote>
                  <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-100 text-sm font-medium text-accent-800">
                      {voice.name.slice(0, 1)}
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-950">{voice.name}</span>
                      <span className="block text-xs text-foreground-500">{voice.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
            <div className="rounded-xl2 border border-secondary-200 bg-secondary-50 px-6 py-10 md:px-12 md:py-14">
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
                <div>
                  <p className="flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-secondary-700">
                    <span className="h-1 w-6 rounded-full bg-secondary-500"></span>
                    CONTACT
                  </p>
                  <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-secondary-950 md:text-3xl">
                    联系我们
                  </h2>
                  <p className="mt-4 max-w-lg text-sm leading-relaxed text-secondary-900/85">
                    想推荐一条值得收录的乐子，或者对内容有不同看法，都欢迎写信给我们。
                    建议在邮件里简单说明内容大致信息，编辑会统一整理与回复。
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <a
                    href={`mailto:${siteInfo.email}`}
                    className="rounded-xl2 border border-secondary-200 bg-background-50 p-5 transition-colors hover:border-secondary-400"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-500 text-background-50">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <p className="mt-3 text-sm font-medium text-foreground-950">内容推荐与反馈</p>
                    <p className="mt-1 break-all text-xs text-foreground-600">{siteInfo.email}</p>
                  </a>
                  <a
                    href={`mailto:${siteInfo.contactEmail}`}
                    className="rounded-xl2 border border-secondary-200 bg-background-50 p-5 transition-colors hover:border-secondary-400"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-500 text-background-50">
                      <i className="ri-customer-service-2-line text-lg"></i>
                    </span>
                    <p className="mt-3 text-sm font-medium text-foreground-950">合作与建议</p>
                    <p className="mt-1 break-all text-xs text-foreground-600">{siteInfo.contactEmail}</p>
                  </a>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-secondary-200 pt-6">
                <Link
                  to="/faq"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-secondary-300 bg-background-50 px-5 py-2.5 text-sm text-secondary-800 transition-colors hover:border-secondary-500"
                >
                  先看看常见问题
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
                >
                  回到首页吃瓜
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}